/**
 * Mapeamento dos contratos de pedido (nest-end-customer-api) para os cards da
 * UI. Puro: compartilhado por "meus pedidos" (`useOrders`) e pela "última
 * compra" do header (`useLastOrder`).
 */

import { toProductCard } from "@/modules/catalog";
import type { MultiProduct } from "@/modules/catalog";
import { roundBRL } from "@/shared/helpers";
import type { EcmOrderStatus, OrderDetails, OrderSummary } from "../types/order.dto";
import type {
    OrderCard,
    OrderItem,
    OrderProgressStep,
    OrderRefundStatus,
    OrderStatus,
} from "../types/orders";

export const PREVIEW_LIMIT = 4;

const dateFmt = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
});
const dateLongFmt = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
});

/** Enum do banco → status exibido no card/badge. */
export function mapStatus(status?: EcmOrderStatus): OrderStatus {
    switch (status) {
        case "PENDING":
            return "aguardando-pagamento";
        case "COMPLETED":
            return "concluido";
        case "CANCELLED":
        case "ANTI_FRAUD_FAILED":
        case "REFUNDED":
            return "cancelado";
        default:
            // APPROVED, SEPARATION, AWAITING_HANDOFF, INVOICE_EMITED
            return "enviado";
    }
}

/** Enum do banco → etapa do stepper (undefined antes da aprovação / cancelado). */
export function mapProgress(status?: EcmOrderStatus): OrderProgressStep | undefined {
    switch (status) {
        case "APPROVED":
            return "pagamento-aprovado";
        case "SEPARATION":
            return "em-separacao";
        case "AWAITING_HANDOFF":
        case "INVOICE_EMITED":
            return "em-rota";
        case "COMPLETED":
            return "concluido";
        default:
            return undefined;
    }
}

const refundOf = (status?: EcmOrderStatus): OrderRefundStatus =>
    status === "REFUNDED" ? "reembolsado" : null;

const formatDate = (iso?: string): string | undefined =>
    iso ? dateFmt.format(new Date(iso)) : undefined;
const formatLongDate = (iso?: string): string | undefined =>
    iso ? dateLongFmt.format(new Date(iso)) : undefined;

/** Summary (lista) → card magro. Os detalhes chegam pela hidratação. */
export function summaryToCard(order: OrderSummary): OrderCard {
    const status = mapStatus(order.status);
    return {
        id: String(order.orderId),
        status,
        rawStatus: order.status,
        refundStatus: refundOf(order.status),
        progressStep: status === "cancelado" ? undefined : mapProgress(order.status),
        dateLabel: formatDate(order.date),
        dateLongLabel: formatLongDate(order.date),
        itemsPreview: [],
        extraCount: 0,
        hydrated: false,
    };
}

/** Detalhes → card completo (itens, total, entrega, resumo, stepper). */
export function detailsToCard(details: OrderDetails): OrderCard {
    const status = mapStatus(details.status);

    const items: OrderItem[] = details.products.map((product) => {
        const model = product.product
            ? toProductCard(product.product as unknown as MultiProduct)
            : undefined;
        const hasDiscount = product.initialPrice > product.finalPrice;
        return {
            id: String(product.orderProductId),
            name: model?.name ?? `Produto ${product.productId}`,
            qty: product.quantity,
            unitPrice: product.finalPrice,
            originalUnitPrice: hasDiscount ? product.initialPrice : undefined,
            href: model?.href,
            imageUrl: model?.image ?? undefined,
            imageAlt: model?.name,
        };
    });

    const subtotal = roundBRL(
        items.reduce((sum, it) => sum + (it.originalUnitPrice ?? it.unitPrice) * it.qty, 0)
    );
    const total = roundBRL(items.reduce((sum, it) => sum + it.unitPrice * it.qty, 0));
    const address = details.shippingAddress;

    return {
        id: String(details.orderId),
        status,
        rawStatus: details.status,
        refundStatus: refundOf(details.status),
        progressStep: status === "cancelado" ? undefined : mapProgress(details.status),
        dateLabel: formatDate(details.date),
        dateLongLabel: formatLongDate(details.date),
        total,
        breakdown: { subtotal, discount: roundBRL(subtotal - total) },
        comment: details.comment,
        items,
        itemCount: items.length,
        itemsPreview: items.slice(0, PREVIEW_LIMIT).map((it) => ({
            id: it.id,
            label: it.name,
            imageUrl: it.imageUrl,
            imageAlt: it.imageAlt,
        })),
        extraCount: Math.max(0, items.length - PREVIEW_LIMIT),
        deliveryAddress: address
            ? {
                  label: details.shippingName || "Endereço de entrega",
                  recipientName: "",
                  recipientPhone: "",
                  street: address,
                  number: details.shippingNumber ?? "",
                  complement: details.shippingComplement,
                  neighborhood: details.shippingNeighborhood,
                  city: details.shippingCity ?? "",
                  state: "",
                  zipCode: details.shippingCep ?? "",
              }
            : undefined,
        hydrated: true,
    };
}
